/**
 * The News Beats home: is the watch running, and what did it bring back?
 *
 * WHAT THIS REPLACES. Every shell used to render `Dashboard`, which is the
 * PUBLISH dashboard — Pending Recommendations, Top Growing Keywords, Keyword
 * Coverage — so a Beats customer's first screen was about keyword coverage and
 * linked out to Trends, a product they had not bought.
 *
 * The first section is the one that did not exist anywhere. A beat whose
 * scheduler quietly stopped looks exactly like a beat with nothing to report: a
 * name, a cadence, and a last briefing that is a bit old. That is the
 * characteristic failure of a standing watch, and until now nothing said so.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';
import { relativeAge } from '../components/common/relativeAge';
import api from '../services/api';
import useBeatRunRefresh from '../components/news-beats/useBeatRunRefresh';
import { cadenceLabel } from './cadence';
import { beatsNeedingYou, recentBriefings, watchSummary } from './beatWatch';
import type { NewsBeatSummary } from '../types';

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
      <p className="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500">
        {label}
      </p>
      <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">{value}</p>
      {hint && <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">{hint}</p>}
    </div>
  );
}

/**
 * News Beats is not being sold yet — it is off the lumnian.com house page. The
 * shell still works for anyone who lands here, but says so up front.
 */
function ComingSoonBanner() {
  return (
    <div className="mb-6 rounded-lg border border-amber-200 dark:border-amber-900/60 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
      <span className="font-semibold">Coming soon.</span> News Beats is not open to new
      accounts yet.
    </div>
  );
}

export default function BeatsHome() {
  const [beats, setBeats] = useState<NewsBeatSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setBeats(await api.listNewsBeats());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your beats');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);
  useBeatRunRefresh(() => void load());

  if (error) {
    return (
      <div>
        <PageHeader title="News Beats" />
        <ComingSoonBanner />
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  if (!beats) {
    return (
      <div>
        <PageHeader title="News Beats" />
        <ComingSoonBanner />
        <p className="text-sm text-gray-400">Loading…</p>
      </div>
    );
  }

  // NOTHING RATHER THAN ZEROES. An account with no beats shown "0 live · 0 due ·
  // 0 stories" reads as a broken screen; what it needs is the one thing to do.
  if (beats.length === 0) {
    return (
      <div>
        <PageHeader
          title="News Beats"
          subtitle="A standing watch on a subject that files a briefing on your cadence."
        />
        <ComingSoonBanner />
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-8 text-center">
          <p className="text-gray-600 dark:text-gray-300">You have no beats yet.</p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            A beat watches a subject and writes you a briefing on a schedule.
          </p>
          <Link
            to="/news-beats"
            className="mt-4 inline-block bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm"
          >
            Set up a beat
          </Link>
        </div>
      </div>
    );
  }

  const problems = beatsNeedingYou(beats);
  const watch = watchSummary(beats);
  // Not the ones already listed under "needs you" — see recentBriefings.
  const recent = recentBriefings(beats, 5, new Set(problems.map((p) => p.beat.id)));

  return (
    <div>
      <PageHeader title="News Beats" />
      <ComingSoonBanner />

      {problems.length > 0 && (
        <section className="mb-8">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
            Needs you
          </h2>
          <ul className="space-y-2">
            {problems.map(({ beat, label, detail, fix }) => (
              <li key={beat.id}>
                <Link
                  to={`/news-beats/${beat.id}`}
                  className="block rounded-lg border border-amber-200 dark:border-amber-900/60 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 hover:border-amber-400"
                >
                  <div className="flex items-baseline gap-3">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {beat.name}
                    </span>
                    <span className="text-sm text-amber-800 dark:text-amber-300">
                      {detail}
                    </span>
                    <span className="flex-1" />
                    {/* Plain words, not the enum. DRAFT / FAILED / OVERDUE told
                        the reader which branch of our code they had landed in. */}
                    <span className="text-[11px] text-amber-700 dark:text-amber-400 whitespace-nowrap">
                      {label}
                    </span>
                  </div>
                  {/* WHAT TO DO, on every row. A list headed "Needs you" that
                      does not say what it needs is a list of worries. */}
                  <div className="mt-1 text-xs text-amber-700/90 dark:text-amber-400/80">
                    {fix}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mb-8">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
          The watch
        </h2>
        <div className="grid gap-4 sm:grid-cols-4">
          <Stat
            label="Running"
            value={String(watch.live)}
            hint={`${watch.live === 1 ? 'beat' : 'beats'} on a schedule`}
          />
          {/* THE question a cadence product answers. */}
          <Stat
            label="Next briefing"
            value={watch.nextDue ? relativeAge(watch.nextDue.toISOString()) : '-'}
            hint={watch.nextDue ? 'from now' : 'nothing scheduled'}
          />
          <Stat label="Stories last round" value={String(watch.storiesLastRound)} />
          {/* "Not watching · 2 draft · 1 paused" said neither what is wrong
              nor whether anything is. Both halves are states somebody chose;
              what they share is that these beats produce nothing. */}
          <Stat
            label="Not running"
            value={String(watch.draft + watch.paused)}
            hint={
              watch.draft && watch.paused
                ? `${watch.draft} never started · ${watch.paused} paused`
                : watch.draft
                  ? `never started${watch.draft === 1 ? '' : ` (${watch.draft})`}`
                  : watch.paused
                    ? `paused${watch.paused === 1 ? '' : ` (${watch.paused})`}`
                    : 'everything is running'
            }
          />
        </div>
      </section>

      {recent.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
            Lately
          </h2>
          <ul className="divide-y divide-gray-200 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            {recent.map((beat) => (
              <li key={beat.id}>
                <Link
                  to={`/news-beats/${beat.id}`}
                  className="flex items-baseline gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {beat.name}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {beat.last_briefing_stories}{' '}
                    {beat.last_briefing_stories === 1 ? 'story' : 'stories'}
                  </span>
                  <span className="flex-1" />
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    {cadenceLabel(beat.interval_minutes)} ·{' '}
                    {relativeAge(beat.last_briefing_at)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
